import ArrowRightRoundedIcon from "@mui/icons-material/ArrowRightRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRightRounded";
import type { SxProps, Theme } from "@mui/material";
import {
  Box,
  Divider,
  List,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { alpha, ThemeProvider, useTheme } from "@mui/material/styles";
import { Fragment, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import type { ClientInfo } from "../../../api/types/clientTypes";
import useClientContext from "../../../context/useClientContext";
import useUserContext from "../../../context/useUserContext";
import storage from "../../../storage/storage";
import { darkTheme } from "../../../themes";
import { getOrganizationClientCode } from "../../../utilities/clientHelper.ts";
import type { Searchable } from "../../../utilities/searchHelper";
import { applySearch } from "../../../utilities/searchHelper";
import { cornersBeforePositions } from "../../common/corners";
import ClientIcon from "./ClientIcon";
import ClientSelectorValue from "./ClientSelectorValue";
import type { GroupSelectionState } from "./client-selector-helper";
import {
  getInitialGroupSelectionState,
  getSortedOrganizationCompanyGroups,
  updateGroupSelectionState,
} from "./client-selector-helper";

const menuItemStyle: SxProps<Theme> = () => ({
  py: 1,
  pl: 4,
});

const subheaderMenuItemStyle: SxProps<Theme> = (theme) => ({
  px: 2,
  py: 1,
  color: theme.palette.text.primary,
});

// Composite the tint over opaque paper so items scrolling behind the sticky subheader don't show through.
const getSubheaderBgStyle = (paperColor: string, highlightColor?: string) => ({
  backgroundColor: paperColor,
  backgroundImage: highlightColor ? `linear-gradient(${highlightColor}, ${highlightColor})` : "none",
});

const listItemTextProps: SxProps<Theme> = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const RECENT_CLIENTS_MIN_COMPANIES = 10;
const RECENT_CLIENTS_COUNT = 3;
const EXTENDED_RECENT_CLIENTS_MIN_COMPANIES = 15;
const EXTENDED_RECENT_CLIENTS_COUNT = 5;

interface Props {
  selectBgColor: string;
  selectHoveredBgColor: string;
}

const ClientSelector = ({ selectBgColor, selectHoveredBgColor }: Props) => {
  const theme = useTheme();

  const { clients, isEntriliaUser } = useUserContext();
  const { clientCode } = useClientContext();
  const navigate = useNavigate();
  const [keyboardHighlightState, setKeyboardHighlightState] = useState<GroupSelectionState>(() =>
    getInitialGroupSelectionState()
  );

  const organizations = useMemo(() => clients.filter((c) => c.type === "FundAdmin"), [clients]);

  const companies = useMemo(
    () =>
      clients
        .filter((c) => c.type === "FundManager")
        .map((client) => ({
          ...client,
          searchTerms: isEntriliaUser
            ? [client.title.toLowerCase(), client.clientCode.toLowerCase()]
            : [client.title.toLowerCase()],
        })),
    [clients, isEntriliaUser]
  );

  const storedRecentClients = useMemo(
    () =>
      storage
        .getRecentClients()
        .map((clientCode) => clients.find((c) => c.clientCode === clientCode))
        .filter(Boolean) as ClientInfo[],
    [clients]
  );

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [filteredCompanies, setFilteredCompanies] = useState<Searchable<ClientInfo>[]>(companies);

  const handleSearch = (term: string) => {
    setFilteredCompanies(applySearch(term, companies, 2));
    setSearchTerm(term);
    setKeyboardHighlightState(getInitialGroupSelectionState());
  };

  const handleCompanyClientSelected = (clientCode: string) => () => {
    storage.addRecentClient(clientCode);
  };

  const organizationCompanyGroups = getSortedOrganizationCompanyGroups(organizations, filteredCompanies);

  const selectedClient = clients.find((c) => c.clientCode === clientCode);

  if (!selectedClient) {
    return null;
  }

  const selectedClientOrganizationCode = getOrganizationClientCode(clientCode, clients);

  const selectedClientOrganization =
    selectedClientOrganizationCode === undefined
      ? undefined
      : clients.find((c) => c.clientCode === selectedClientOrganizationCode);

  const recentClientsCount =
    companies.length >= EXTENDED_RECENT_CLIENTS_MIN_COMPANIES
      ? EXTENDED_RECENT_CLIENTS_COUNT
      : companies.length >= RECENT_CLIENTS_MIN_COMPANIES
        ? RECENT_CLIENTS_COUNT
        : 0;
  const showRecentClients = recentClientsCount > 0 && !searchTerm;
  const recentClients = showRecentClients ? storedRecentClients.slice(0, recentClientsCount) : [];

  const groupNodeLengths = organizationCompanyGroups.map((group) => group.companies.length);

  const highlightedGroup = organizationCompanyGroups[keyboardHighlightState[0]];
  const highlightedCompany = highlightedGroup?.companies[keyboardHighlightState[1]];
  const highlightedOrganization = highlightedCompany !== undefined ? undefined : highlightedGroup?.organization;

  const handleHighlightPreviousClient = () => {
    setKeyboardHighlightState((current) => updateGroupSelectionState(current, groupNodeLengths, -1));
  };

  const handleHighlightNextClient = () => {
    setKeyboardHighlightState((current) => updateGroupSelectionState(current, groupNodeLengths, 1));
  };

  const handleSelectHighlightedClient = () => {
    const client = highlightedCompany ?? highlightedOrganization;
    if (client === undefined) {
      return;
    }

    if (client.type === "FundManager") {
      handleCompanyClientSelected(client.clientCode)();
    }

    navigate(`/${client.clientCode}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === "Escape") {
      return;
    }

    e.stopPropagation();
    if (showRecentClients || !searchTerm) {
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        handleHighlightNextClient();
        return;
      case "ArrowUp":
        e.preventDefault();
        handleHighlightPreviousClient();
        return;
      case "Enter":
        e.preventDefault();
        handleSelectHighlightedClient();
        return;
    }
  };

  const handleOpen = () => {
    setKeyboardHighlightState(getInitialGroupSelectionState());
    setFilteredCompanies(companies);
    setOpen(true);
  };

  const selectedItemColor = alpha(theme.palette.primary.main, 0.08);
  const hoveredSelectedItemColor = alpha(theme.palette.primary.main, 0.12);
  const hoveredItemColor = theme.palette.action.hover;

  return (
    <Select
      open={open}
      variant="outlined"
      readOnly={clients.length <= 1}
      onOpen={handleOpen}
      onClose={() => setOpen(false)}
      value={[clientCode]}
      multiple
      IconComponent={ArrowRightRoundedIcon}
      renderValue={() => (
        <ThemeProvider theme={darkTheme}>
          <ClientSelectorValue client={selectedClient} clientOrganization={selectedClientOrganization} />
        </ThemeProvider>
      )}
      sx={(theme) => ({
        width: theme.spacing(32),
        px: 0,
        ".MuiSelect-select": { pl: 1.75, py: 1 },
        ".MuiOutlinedInput-input": {
          backgroundColor: selectBgColor,
          ":hover": {
            bgcolor: selectHoveredBgColor,
          },
        },
        ".MuiOutlinedInput-notchedOutline": { border: "none" },
        "& .MuiSelect-icon": { mr: 0.7 },
      })}
      MenuProps={{
        anchorOrigin: {
          vertical: "center",
          horizontal: "right",
        },
        transformOrigin: {
          vertical: "center",
          horizontal: "left",
        },
        disableRestoreFocus: true, //Prevents the autofocus of the SearchField from not working
        autoFocus: false, // Otherwise the menu list takes focus away from the SearchField
        slotProps: {
          paper: {
            elevation: 0,
            sx: (theme) => ({
              minWidth: theme.spacing(32),
              width: "auto",
              overflow: "visible",
              "&:before": cornersBeforePositions.left("top", 60),
              marginLeft: theme.spacing(2),
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              maxHeight: "100%",
            }),
          },
        },
      }}
    >
      <Box sx={{ mx: 2, mt: 1, mb: 2 }}>
        <SearchField onSearch={handleSearch} fullWidth onKeyDown={handleKeyDown} autoFocus />
      </Box>
      <Divider />
      <List
        onMouseEnter={() => setKeyboardHighlightState(getInitialGroupSelectionState())}
        sx={(t) => ({
          overflowX: "hidden",
          overflowY: "auto",
          maxHeight: "calc(86vh)",
          width: t.spacing(45),
          "& ul": { padding: 0 },
          p: 0,
          my: 1,
        })}
      >
        <Box component="li" key="recent">
          <Box component="ul">
            {recentClients.length > 0 && <ListSubheader sx={subheaderMenuItemStyle}>Recent</ListSubheader>}
            {recentClients.map((client) => (
              <MenuItem
                key={client.clientCode + "_recent"}
                component={Link}
                to={`/${client.clientCode}`}
                onClick={handleCompanyClientSelected(client.clientCode)}
                sx={menuItemStyle}
              >
                <ListItemIcon>
                  <ClientIcon clientType={client.type} logoUrl={client.branding.logoMarkUrl} />
                </ListItemIcon>
                <ListItemText
                  slotProps={{
                    primary: { sx: listItemTextProps },
                  }}
                >
                  {client.title}
                </ListItemText>
                <ChevronRightIcon color="secondary" />
              </MenuItem>
            ))}
          </Box>
        </Box>

        {organizationCompanyGroups.length === 0 ? (
          <Typography color="textSecondary" sx={{ m: 2 }}>
            No companies found.
          </Typography>
        ) : (
          organizationCompanyGroups.map((clientGroup, i) => {
            return (
              <Fragment key={clientGroup.organization?.clientCode ?? "unmanaged"}>
                {(i > 0 || recentClients.length > 0) && <Divider component="li" sx={{ my: 0.5 }} />}
                <Box component="li">
                  <Box component="ul">
                    {clientGroup.organization !== undefined && (
                      <ListSubheader
                        key={clientGroup.organization.clientCode}
                        component={Link}
                        to={`/${clientGroup.organization.clientCode}`}
                        sx={(t) => ({
                          ...subheaderMenuItemStyle(t),
                          textDecoration: "none",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          ...getSubheaderBgStyle(
                            t.palette.background.paper,
                            clientGroup.organization?.clientCode === clientCode
                              ? selectedItemColor
                              : clientGroup.organization?.clientCode === highlightedOrganization?.clientCode
                                ? hoveredItemColor
                                : undefined
                          ),
                          ":hover": getSubheaderBgStyle(
                            t.palette.background.paper,
                            clientGroup.organization?.clientCode === clientCode
                              ? hoveredSelectedItemColor
                              : hoveredItemColor
                          ),
                        })}
                      >
                        {clientGroup.organization.title}
                        <ChevronRightIcon color="secondary" />
                      </ListSubheader>
                    )}
                    {clientGroup.companies.map((company) => (
                      <MenuItem
                        component={Link}
                        to={`/${company.clientCode}`}
                        key={company.clientCode}
                        onClick={handleCompanyClientSelected(company.clientCode)}
                        sx={(t) => ({
                          ...menuItemStyle(t),
                          bgcolor:
                            company.clientCode === clientCode
                              ? selectedItemColor
                              : company.clientCode === highlightedCompany?.clientCode &&
                                  clientGroup.organization?.clientCode === highlightedGroup?.organization?.clientCode
                                ? hoveredItemColor
                                : undefined,
                          ":hover": {
                            bgcolor: company.clientCode === clientCode ? hoveredSelectedItemColor : undefined,
                          },
                        })}
                      >
                        <ListItemIcon>
                          <ClientIcon clientType={company.type} logoUrl={company.branding.logoMarkUrl} />
                        </ListItemIcon>
                        <ListItemText
                          slotProps={{
                            primary: { sx: listItemTextProps },
                          }}
                        >
                          {company.title}
                        </ListItemText>
                        <ChevronRightIcon color="secondary" />
                      </MenuItem>
                    ))}
                  </Box>
                </Box>
              </Fragment>
            );
          })
        )}
      </List>
    </Select>
  );
};

export default ClientSelector;
